/**
 * useViewMode - Hook para alternar entre Modo Simples (leigo/CEO) e Avançado (especialista).
 *
 * Persiste preferência por usuário em localStorage. Aplica-se globalmente
 * (topbar) e cada componente decide como adaptar sua densidade.
 *
 * Modo Simples: linguagem de negócio, esconde jargão, foca em $ e ROI.
 * Modo Avançado: terminologia técnica (ROAS, CAC, CPL, CR1-4), métricas extras.
 */

import { useEffect, useState, useCallback } from 'react'

const STORAGE_KEY = 'deebs:viewMode'
const DEFAULT_MODE = 'simple' // padrão para CEO/leigo

const listeners = new Set()

function readStored() {
  if (typeof window === 'undefined') return DEFAULT_MODE
  const v = window.localStorage.getItem(STORAGE_KEY)
  return v === 'advanced' ? 'advanced' : 'simple'
}

function writeStored(mode) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, mode)
}

export function useViewMode() {
  const [mode, setModeState] = useState(readStored)

  useEffect(() => {
    const fn = (next) => setModeState(next)
    listeners.add(fn)
    return () => listeners.delete(fn)
  }, [])

  const setMode = useCallback((next) => {
    const value = next === 'advanced' ? 'advanced' : 'simple'
    writeStored(value)
    setModeState(value)
    listeners.forEach((fn) => fn(value))
  }, [])

  const toggle = useCallback(() => {
    setMode(mode === 'simple' ? 'advanced' : 'simple')
  }, [mode, setMode])

  return {
    mode,
    isSimple: mode === 'simple',
    isAdvanced: mode === 'advanced',
    setMode,
    toggle,
  }
}
